import React, { useState } from 'react';
import { ActionConfig, ActionConditions, Component, FormValues } from '@/types/platform';
import ComponentRenderer from './ComponentRenderer.tsx';
import './ActionButton.css';

interface ViewState {
  hasResult: boolean;
  hasSelectedFiles: boolean;
  insertedToExcel: boolean;
  formValid: boolean;
  selectedFileExtensions: Set<string>;
  selectedDocumentType?: number | string;
}

interface ActionButtonProps {
  action: ActionConfig;
  viewState: ViewState;
  isSubmitting: string | null;
  getTranslation: (key: string) => string;
  onExecute: (action: ActionConfig, panelFormValues?: FormValues, panelFiles?: File[]) => Promise<void>;
  platformId: string;
  /** Controls trigger button appearance. 'result' = full-width colored btn; 'top' = small bordered btn */
  context?: 'result' | 'top';
  /**
   * When true, only renders the trigger button (no inline panel).
   * The panel itself should be rendered separately via renderPanel().
   */
  triggerOnly?: boolean;
  /** When true, only renders the panel (no trigger button) */
  panelOnly?: boolean;
  /** Shared open state — used when panel is rendered outside the button row */
  panelOpen?: boolean;
  onPanelOpen?: () => void;
  onPanelClose?: () => void;
}

/** Standalone panel renderer — used by DynamicPlatformView to render panels below the button row */
export interface ActionPanelHandle {
  isOpen: boolean;
  renderPanel: () => React.ReactNode;
}

function evalConditions(cond: ActionConditions | undefined, state: ViewState & { hasSelectedPanelFiles?: boolean; panelOpen?: boolean; panelFormValid?: boolean }): boolean {
  if (!cond) return true;

  // If anyOf is present, use OR logic: at least one sub-condition must pass
  if (cond.anyOf && cond.anyOf.length > 0) {
    return cond.anyOf.some((sub) => evalConditions(sub, state));
  }

  if (cond.hasResult !== undefined && cond.hasResult !== state.hasResult) return false;
  if (cond.hasSelectedFiles !== undefined && cond.hasSelectedFiles !== state.hasSelectedFiles) return false;
  if (cond.hasSelectedPanelFiles !== undefined && cond.hasSelectedPanelFiles !== (state.hasSelectedPanelFiles ?? false)) return false;
  if (cond.insertedToExcel !== undefined && cond.insertedToExcel !== state.insertedToExcel) return false;
  if (cond.hasSpreadsheetData !== undefined && cond.hasSpreadsheetData !== (state as any).hasSpreadsheetData) return false;
  if (cond.formValid !== undefined && cond.formValid !== state.formValid) return false;
  if (cond.panelOpen !== undefined && cond.panelOpen !== (state.panelOpen ?? false)) return false;
  if (cond.panelFormValid !== undefined && cond.panelFormValid !== (state.panelFormValid ?? false)) return false;
  if (cond.selectedFileExtensionIn) {
    const hasMatch = cond.selectedFileExtensionIn.some(ext => state.selectedFileExtensions.has(ext));
    if (!hasMatch) return false;
  }
  if (cond.selectedDocumentType !== undefined) {
    if (String(cond.selectedDocumentType) !== String((state as any).selectedDocumentType)) return false;
  }
  return true;
}

function variantClass(variant?: string, actionId?: string): string {
  // Specific action IDs that always use purple regardless of server-side variant
  if (actionId === 'convert_to_sale' || actionId === 'submit_convert_to_sale') {
    return 'result-btn result-btn-purple';
  }
  switch (variant) {
    case 'primary':   return 'result-btn result-btn-excel';
    case 'success':   return 'result-btn result-btn-csv';
    case 'secondary': return 'result-btn result-btn-new';
    case 'danger':    return 'result-btn result-btn-danger';
    case 'purple':    return 'result-btn result-btn-purple';
    default:          return 'result-btn result-btn-new';
  }
}

const ActionButton: React.FC<ActionButtonProps> = ({
  action,
  viewState,
  isSubmitting,
  getTranslation,
  onExecute,
  context = 'result',
  triggerOnly = false,
  panelOnly = false,
  panelOpen: externalPanelOpen,
  onPanelOpen,
  onPanelClose,
}) => {
  const [internalPanelOpen, setInternalPanelOpen] = useState(false);
  const [panelFormValues, setPanelFormValues] = useState<FormValues>({});
  const [panelFiles, setPanelFiles] = useState<FormValues>({});
  const [panelError, setPanelError] = useState<string | null>(null);
  const [panelSuccess, setPanelSuccess] = useState(false);

  // Use external open state if provided (for out-of-row panel rendering)
  const panelOpen = externalPanelOpen !== undefined ? externalPanelOpen : internalPanelOpen;

  const isVisible = evalConditions(action.visibleWhen, viewState);
  if (!isVisible) return null;

  const hasPanelFiles = Object.values(panelFiles).some((f: any) => Array.isArray(f) && f.length > 0);
  const panelState = { ...viewState, hasSelectedPanelFiles: hasPanelFiles, panelOpen, panelFormValid: true };

  const isEnabled = evalConditions(action.enabledWhen, panelState);
  const isLoading = isSubmitting === action.id;

  const triggerClass = context === 'top'
    ? `top-action-btn top-action-btn--${action.variant ?? 'secondary'}`
    : variantClass(action.variant, action.id);

  // ─── Simple button (no panel) ─────────────────────────────────────────────
  if (action.type !== 'panelAction' || !action.panel) {
    return (
      <button
        type="button"
        className={triggerClass}
        disabled={!isEnabled || isSubmitting !== null}
        onClick={() => onExecute(action)}
      >
        {isLoading ? getTranslation('form.submitting') : getTranslation(action.labelKey)}
      </button>
    );
  }

  // ─── Panel action ─────────────────────────────────────────────────────────
  const panel = action.panel;

  const openPanel = () => {
    const defaults: FormValues = {};
    panel.components.forEach(comp => {
      if (comp.defaultValue !== undefined) {
        defaults[comp.name] = comp.defaultValue === 'today'
          ? new Date().toISOString().split('T')[0]
          : comp.defaultValue;
      } else {
        defaults[comp.name] = '';
      }
    });
    setPanelFormValues(defaults);
    setPanelFiles({});
    setPanelError(null);
    setPanelSuccess(false);
    if (onPanelOpen) {
      onPanelOpen();
    } else {
      setInternalPanelOpen(true);
    }
  };

  const closePanel = () => {
    setPanelError(null);
    setPanelSuccess(false);
    if (onPanelClose) {
      onPanelClose();
    } else {
      setInternalPanelOpen(false);
    }
  };

  const handlePanelValueChange = (name: string, value: any) =>
    setPanelFormValues(prev => ({ ...prev, [name]: value }));

  const handlePanelFileChange = (name: string, files: File[]) =>
    setPanelFiles(prev => ({ ...prev, [name]: files }));

  const handlePanelAction = async (panelAction: ActionConfig) => {
    setPanelError(null);
    try {
      const allPanelFiles = Object.values(panelFiles).flat() as File[];
      await onExecute(panelAction, panelFormValues, allPanelFiles);
      setPanelSuccess(true);
    } catch (err: any) {
      setPanelError(err?.message ?? 'An error occurred.');
    }
  };

  const gridStyle = panel.layout === 'grid'
    ? { display: 'grid', gridTemplateColumns: `repeat(${panel.columns ?? 1}, 1fr)`, gap: '12px' }
    : { display: 'flex', flexDirection: 'column' as const, gap: '12px' };

  const panelNode = panelOpen ? (
    <div className="action-panel">
      <div className="action-panel-header">
        <div>
          <div className="action-panel-title">{getTranslation(panel.titleKey)}</div>
          {panel.descriptionKey && (
            <div className="action-panel-description">{getTranslation(panel.descriptionKey)}</div>
          )}
        </div>
        <button className="action-panel-close" onClick={closePanel} aria-label="Close">✕</button>
      </div>

      {panelSuccess ? (
        <div className="action-panel-success">
          <span>✓</span>
          {getTranslation('topAction.productList.successMessage')}
        </div>
      ) : (
        <>
          <div style={gridStyle}>
            {panel.components.map((comp: Component, idx: number) => (
              <ComponentRenderer
                key={`${comp.name}-${idx}`}
                component={comp}
                value={panelFormValues[comp.name]}
                files={(panelFiles[comp.name] as File[]) || []}
                formValues={panelFormValues}
                onValueChange={handlePanelValueChange}
                onFileChange={handlePanelFileChange}
                getTranslation={getTranslation}
              />
            ))}
          </div>

          {panelError && <div className="action-panel-error">{panelError}</div>}

          <div className="action-panel-footer">
            {panel.actions.map(panelAction => {
              const panelActionState = { ...viewState, hasSelectedPanelFiles: hasPanelFiles, panelOpen: true, panelFormValid: true };
              const panelActionVisible = evalConditions(panelAction.visibleWhen, panelActionState);
              const panelActionEnabled = evalConditions(panelAction.enabledWhen, panelActionState);
              if (!panelActionVisible) return null;
              return (
                <button
                  key={panelAction.id}
                  type="button"
                  className={variantClass(panelAction.variant)}
                  disabled={!panelActionEnabled || isSubmitting !== null}
                  onClick={() => handlePanelAction(panelAction)}
                >
                  {isSubmitting === panelAction.id
                    ? getTranslation('form.submitting')
                    : getTranslation(panelAction.labelKey)}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  ) : null;

  // triggerOnly mode: just render the button, panel is rendered elsewhere
  if (triggerOnly) {
    return (
      <button
        type="button"
        className={triggerClass}
        disabled={!isEnabled || isSubmitting !== null}
        onClick={panelOpen ? closePanel : openPanel}
      >
        {getTranslation(action.labelKey)}
      </button>
    );
  }

  // panelOnly mode: just render the panel (used when rendered below the button row)
  if (panelOnly) {
    return panelNode;
  }

  return (
    <div className="action-panel-wrapper">
      <button
        type="button"
        className={triggerClass}
        disabled={!isEnabled || isSubmitting !== null}
        onClick={panelOpen ? closePanel : openPanel}
      >
        {getTranslation(action.labelKey)}
      </button>
      {panelNode}
    </div>
  );
};

export default ActionButton;
export { evalConditions as evalActionConditions };
